import type { RawComponent } from "../types.js";

export function collectCanvas(): RawComponent {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 220;
    canvas.height = 40;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { name: "canvas_hash", rawValue: null };

    ctx.textBaseline = "top";
    ctx.font = "16px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillRect(0, 0, 100, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("verify-anti-alt 😀", 2, 15);
    ctx.strokeStyle = "rgba(120, 20, 200, 0.7)";
    ctx.beginPath();
    ctx.arc(180, 20, 15, 0, Math.PI * 2);
    ctx.stroke();

    return { name: "canvas_hash", rawValue: canvas.toDataURL() };
  } catch {
    return { name: "canvas_hash", rawValue: null };
  }
}

export function collectWebgl(): RawComponent[] {
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl") ??
      canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) {
      return [
        { name: "webgl_hash", rawValue: null },
        { name: "webgl_vendor", rawValue: null },
        { name: "webgl_renderer", rawValue: null },
      ];
    }

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = debugInfo
      ? String(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL))
      : String(gl.getParameter(gl.VENDOR));
    const renderer = debugInfo
      ? String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL))
      : String(gl.getParameter(gl.RENDERER));

    const extensions = (gl.getSupportedExtensions() ?? []).sort().join(",");
    const params = [
      gl.getParameter(gl.VERSION),
      gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
      gl.getParameter(gl.MAX_TEXTURE_SIZE),
      gl.getParameter(gl.MAX_VIEWPORT_DIMS)?.toString?.(),
    ].join("|");

    return [
      { name: "webgl_hash", rawValue: `${vendor}|${renderer}|${params}|${extensions}` },
      { name: "webgl_vendor", rawValue: vendor },
      { name: "webgl_renderer", rawValue: renderer },
    ];
  } catch {
    return [
      { name: "webgl_hash", rawValue: null },
      { name: "webgl_vendor", rawValue: null },
      { name: "webgl_renderer", rawValue: null },
    ];
  }
}

// GPUAdapterInfo and GPUSupportedLimits expose their fields as getters on
// the interface prototype, not as own enumerable properties, so
// JSON.stringify(adapter.info) / JSON.stringify(adapter.limits) silently
// collapse to "{}" for every real adapter. Read each field out by name.
const GPU_ADAPTER_INFO_FIELDS = ["vendor", "architecture", "device", "description"] as const;

const GPU_LIMIT_FIELDS = [
  "maxTextureDimension1D",
  "maxTextureDimension2D",
  "maxTextureDimension3D",
  "maxTextureArrayLayers",
  "maxBindGroups",
  "maxBindGroupsPlusVertexBuffers",
  "maxBindingsPerBindGroup",
  "maxDynamicUniformBuffersPerPipelineLayout",
  "maxDynamicStorageBuffersPerPipelineLayout",
  "maxSampledTexturesPerShaderStage",
  "maxSamplersPerShaderStage",
  "maxStorageBuffersPerShaderStage",
  "maxStorageTexturesPerShaderStage",
  "maxUniformBuffersPerShaderStage",
  "maxUniformBufferBindingSize",
  "maxStorageBufferBindingSize",
  "minUniformBufferOffsetAlignment",
  "minStorageBufferOffsetAlignment",
  "maxVertexBuffers",
  "maxBufferSize",
  "maxVertexAttributes",
  "maxVertexBufferArrayStride",
  "maxInterStageShaderComponents",
  "maxInterStageShaderVariables",
  "maxColorAttachments",
  "maxColorAttachmentBytesPerSample",
  "maxComputeWorkgroupStorageSize",
  "maxComputeInvocationsPerWorkgroup",
  "maxComputeWorkgroupSizeX",
  "maxComputeWorkgroupSizeY",
  "maxComputeWorkgroupSizeZ",
  "maxComputeWorkgroupsPerDimension",
] as const;

function pickFields(source: Record<string, unknown>, fields: readonly string[]): Record<string, unknown> {
  const picked: Record<string, unknown> = {};
  for (const field of fields) {
    picked[field] = source[field] ?? null;
  }
  return picked;
}

export async function collectWebgpu(): Promise<RawComponent> {
  try {
    const gpu = (navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
    if (!gpu) return { name: "webgpu_hash", rawValue: null };
    const adapter = (await gpu.requestAdapter()) as
      | { info?: Record<string, unknown>; limits?: Record<string, unknown> }
      | null;
    if (!adapter) return { name: "webgpu_hash", rawValue: null };

    const info = adapter.info ? pickFields(adapter.info, GPU_ADAPTER_INFO_FIELDS) : null;
    const limits = adapter.limits ? pickFields(adapter.limits, GPU_LIMIT_FIELDS) : null;

    return { name: "webgpu_hash", rawValue: JSON.stringify({ info, limits }) };
  } catch {
    return { name: "webgpu_hash", rawValue: null };
  }
}
